import { FunctionComponent, useMemo, useState } from 'react';
import { Moment } from 'moment-timezone';
import { InvoiceListRecord } from '@spike/invoice-model';
import { CalendarField2 } from 'components/UI';
import Calendar from 'components/UI/CalendarField2/Calendar';
import Button from 'components/UI/V3/Button/Button';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import { useMediaQuery } from '@material-ui/core';
import { mediaQueries } from 'hooks/useBreakpoints';
import styles from './EditInvoiceDateModal.module.scss';

export interface EditInvoiceDateModalProps {
    invoice: InvoiceListRecord | null;
    saving?: boolean;
    zIndex?: number;
    onClose: () => void;
    onSave: (date: Moment) => void;
}

const EditInvoiceDateModalContent: FunctionComponent<{
    invoice: InvoiceListRecord;
    saving?: boolean;
    zIndex?: number;
    onClose: () => void;
    onSave: (date: Moment) => void;
}> = ({ invoice, saving = false, zIndex, onClose, onSave }) => {
    const isMobile = useMediaQuery(`(max-width:${mediaQueries.down.sm})`);
    const [selectedDate, setSelectedDate] = useState<Moment>(() => invoice.date.clone());

    const isValid = useMemo(() => selectedDate?.isValid() ?? false, [selectedDate]);
    const saveDisabled = saving || !isValid;

    const handleSave = () => {
        if (!isValid) {
            return;
        }

        const dateWithTime = selectedDate
            .clone()
            .hour(invoice.date.hour())
            .minute(invoice.date.minute())
            .second(invoice.date.second());

        onSave(dateWithTime);
    };

    return (
        <GenericModal
            fullScreen={isMobile}
            zIndex={zIndex}
            onClose={onClose}
            footer={
                <div className={styles.footer}>
                    <div className={styles.footerAction}>
                        <Button
                            size="large"
                            className={styles.button}
                            label="Cancel"
                            onClick={onClose}
                            variant="black"
                            disabled={saving}
                        />
                    </div>
                    <div className={styles.footerAction}>
                        <Button
                            label={saving ? 'Saving...' : 'Save'}
                            size="large"
                            className={styles.button}
                            onClick={handleSave}
                            variant="green"
                            isLoading={saving}
                            disabled={saveDisabled}
                        />
                    </div>
                </div>
            }
            className={styles.modal}
        >
            <div className={styles.content}>
                <span className={styles.title}>Edit Invoice Date</span>

                {isMobile ? (
                    <div className={styles.mobileDateSection}>
                        <span className={styles.dateLabel}>Date</span>
                        <div className={styles.dateInput}>{selectedDate.format('MM/DD/YYYY')}</div>
                        <Calendar
                            date={selectedDate}
                            inline
                            onChange={moment => setSelectedDate(moment)}
                            className={styles.inlineCalendar}
                        />
                    </div>
                ) : (
                    <CalendarField2
                        label="Date"
                        placeholder="Date"
                        name="invoiceDate"
                        date={selectedDate}
                        displayFormat="MM/DD/YYYY"
                        hideCalendarIcon
                        useChevronIcon
                        onChange={moment => moment && setSelectedDate(moment)}
                        disabled={saving}
                        className={styles.dateField}
                    />
                )}
            </div>
        </GenericModal>
    );
};

export const EditInvoiceDateModal: FunctionComponent<EditInvoiceDateModalProps> = ({
    invoice,
    saving,
    zIndex,
    onClose,
    onSave
}) => {
    if (!invoice) {
        return null;
    }

    return (
        <EditInvoiceDateModalContent
            key={invoice.uuid}
            invoice={invoice}
            saving={saving}
            zIndex={zIndex}
            onClose={onClose}
            onSave={onSave}
        />
    );
};

export default EditInvoiceDateModal;
